/**
 * API Service
 * High-level API functions using the HTTP client
 */

import { FormData, AnalysisResponse, FormSchemaResponse } from '../types';
import { apiClient } from './api/client';
import { ApiError } from './api/errors';
import { API_CONFIG } from './api/config';
API_CONFIG.ENDPOINTS.ANALYZE

// Setup auth token interceptor
const TOKEN_KEY = 'dc-copilot-token';
apiClient.getInterceptors().addRequestInterceptor((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  return config;
});

/**
 * Transform form data for backend API
 * Maps frontend field names to backend field names and handles type conversions
 */
function transformFormDataForBackend(formData: FormData): any {
  const transformed: any = {};

  // Field name mappings (frontend → backend)
  const fieldMappings: Record<string, string> = {
    'weight_kg': 'weight',
    'height_feet': 'height_ft',
    'height_inches': 'height_in',
    'alcohol_frequency': 'alcohol_consumption',
    'smoking_frequency': 'smoking_status',
    'additional_information': 'additional_info',
    'pe_partner_ejaculation_time': 'pe_partner_time_to_ejaculation'
  };

  // Fields to convert to integers
  const intFields = ['age', 'height_ft', 'height_in'];

  // Fields to convert to floats
  const floatFields = ['weight', 'height_cm'];

  // Fields to ensure are arrays
  const arrayFields = ['medical_conditions', 'current_medications', 'previous_treatments'];

  // Process each field
  for (const [key, value] of Object.entries(formData)) {
    // Map field name if needed
    const mappedKey = fieldMappings[key] || key;

    // SPECIAL HANDLING: Array fields must always be arrays
    if (arrayFields.includes(mappedKey)) {
      if (Array.isArray(value)) {
        transformed[mappedKey] = value;
      } else if (value === '' || value === null || value === undefined) {
        transformed[mappedKey] = [];
      } else {
        transformed[mappedKey] = [value];
      }
      continue;
    }

    // Handle empty values for non-array fields
    if (value === '' || value === null || value === undefined) {
      transformed[mappedKey] = null;
      continue;
    }

    // Convert to appropriate type
    if (intFields.includes(mappedKey)) {
      transformed[mappedKey] = parseInt(value as string, 10);
    } else if (floatFields.includes(mappedKey)) {
      transformed[mappedKey] = parseFloat(value as string);
    } else {
      transformed[mappedKey] = value;
    }
  }

  // Add metadata
  transformed.form_version = '2.2';
  transformed.submitted_at = new Date().toISOString();

  return transformed;
}

/**
 * Analyze case - Submit form data for AI analysis
 * @param formData - Form data to analyze
 * @returns Analysis response with diagnosis and recommendations
 * @throws ApiError if request fails
 */
export async function analyzeCase(formData: FormData): Promise<AnalysisResponse> {
  try {
    const transformedData = transformFormDataForBackend(formData);
    
    const response = await apiClient.post<AnalysisResponse>(
      'ANALYZE',
      transformedData,
      {
        timeout: 60000, // 60 seconds for analysis
        retry: true,
        retryAttempts: 2, // Retry once for long-running analysis
      }
    );

    console.log(response);
    return response.data;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Wrap unknown errors
    throw ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Unknown error occurred')
    );
  }
}

/**
 * Check API health status
 * @returns true if API is healthy, false otherwise
 */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await apiClient.get('HEALTH', {
      timeout: 5000, // 5 seconds for health check
      retry: true,
      retryAttempts: 2,
    });

    return response.status === 200;
  } catch (error) {
    // Health check failures are not critical, just return false
    return false;
  }
}

/**
 * Export API client for advanced usage
 */
export { apiClient };

/**
 * Export error classes for error handling
 */
export { ApiError } from './api/errors';

/**
 * Login request/response types
 */
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  statusCode: number;
  message: string;
  data?: {
    token: string;
  };
}

/**
 * Login - Authenticate user and get token
 * @param credentials - Login credentials (email and password)
 * @returns Login response with token
 * @throws ApiError if login fails
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  try {
    // Use auth base URL for login endpoint
    const authBaseUrl = API_CONFIG.AUTH_BASE_URL;
    const loginUrl = `${authBaseUrl}/api/v1/admin/dc-copilot-login`;
    
    const response = await fetch(loginUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data: LoginResponse = await response.json();

    // Check statusCode in response body (API returns statusCode in body)
    if (data.statusCode !== 200) {
      throw new ApiError(
        data.message || 'Login failed',
        data.statusCode,
        response.statusText,
        data
      );
    }

    // Also check HTTP response status
    if (!response.ok) {
      throw new ApiError(
        data.message || 'Login failed',
        response.status,
        response.statusText,
        data
      );
    }

    return data;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Wrap unknown errors
    throw ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Login request failed')
    );
  }
}

/**
 * Appointment types
 */
export interface Appointment {
  id: number;
  user_package_id: number | null;
  discovery_call_booking_id: number;
  patient_id: number;
  doctor_id: number;
  discount_id: number | null;
  date: string;
  time: string;
  fees: number | null;
  discount: number;
  status: string;
  isReScheduled: number;
  isPReminderSent: number;
  isDReminderSent: number;
  meeting_id: string | null;
  password: string | null;
  primary_issue: string | null;
  next_session_date: string | null;
  comments: string | null;
  platform: string;
  noShow: number;
  createdAt: string;
  updatedAt: string;
  max_duration_in_min: number;
  remaining_session_count: number | null;
  total_session_count: number | null;
  isPRMorningReminderSent: number;
  isDRMorningReminderSent: number;
  milestone_description: string | null;
  type: string;
  milestone_id: number | null;
  unique_token: string | null;
  milestone_rating: number;
  is15MinPReminderSent: number;
  package_name: string | null;
  doctor_name: string;
  salutations: string | null;
  profile_pic: string | null;
  refund_amount: number | null;
  remaining_time: number;
  patient: {
    id: number;
    name: string;
    mobile: string;
  };
}

export interface AppointmentsResponse {
  statusCode: number;
  message: string;
  data: Appointment[];
}

/**
 * Treatment Packages
 */
export interface TreatmentPackage {
  id: number;
  name: string;
  meta_description?: string | null;
  meta_tittle?: string | null;
  slug?: string;
  price: number;
  original_price?: number | null;
  validity?: number | null;
  sexologist_consultations?: number | null;
  therapist_consultations?: number | null;
  counsellor_consultations?: number | null;
  position?: string | null;
  packageStatus?: string | null;
}

export interface PackageListResponse {
  statusCode: number;
  message: string;
  data: TreatmentPackage[];
}

/**
 * Get DC Copilot Appointments
 * @returns Appointments response with list of appointments
 * @throws ApiError if request fails
 */
export async function getAppointments(): Promise<AppointmentsResponse> {
  try {
    // Use auth base URL for appointments endpoint
    const authBaseUrl = API_CONFIG.AUTH_BASE_URL;
    const appointmentsUrl = `${authBaseUrl}/api/v1/admin/getDcCopliotAppoinments`;
    
    const response = await fetch(appointmentsUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('dc-copilot-token')}`,
      },
    });

    const data: AppointmentsResponse = await response.json();

    // Check statusCode in response body
    if (data.statusCode !== 200) {
      throw new ApiError(
        data.message || 'Failed to fetch appointments',
        data.statusCode,
        response.statusText,
        data
      );
    }

    // Also check HTTP response status
    if (!response.ok) {
      throw new ApiError(
        data.message || 'Failed to fetch appointments',
        response.status,
        response.statusText,
        data
      );
    }

    return data;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Wrap unknown errors
    throw ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Failed to fetch appointments')
    );
  }
}

/**
 * Get Treatment Packages
 * @returns Package list response
 * @throws ApiError if request fails
 */
export async function getTreatmentPackages(): Promise<PackageListResponse> {
  try {
    const authBaseUrl = API_CONFIG.AUTH_BASE_URL;
    const packageUrl = `${authBaseUrl}/api/v1/user/packageList`;
    
    const response = await fetch(packageUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('dc-copilot-token')}`,
      },
    });

    const data: PackageListResponse = await response.json();

    if (data.statusCode !== 200) {
      throw new ApiError(
        data.message || 'Failed to fetch treatment packages',
        data.statusCode,
        response.statusText,
        data
      );
    }

    if (!response.ok) {
      throw new ApiError(
        data.message || 'Failed to fetch treatment packages',
        response.status,
        response.statusText,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Failed to fetch treatment packages')
    );
  }
}

/**
 * Language type for form schema
 */
export type FormLanguage = 'english' | 'hindi';

/**
 * Get Form Schema - Fetch complete form structure for dynamic rendering
 * @param language - Language for form labels (english or hindi)
 * @returns Form schema response with all questions, options, and conditional rules
 * @throws ApiError if request fails
 */
export async function getFormSchema(language: FormLanguage = 'english'): Promise<FormSchemaResponse> {
  try {
    const authBaseUrl = API_CONFIG.AUTH_BASE_URL;
    const schemaUrl = `${authBaseUrl}/api/v1/admin/dc-copilot-form-schema?language=${language}`;
    
    const response = await fetch(schemaUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('dc-copilot-token')}`,
      },
    });

    const data: FormSchemaResponse = await response.json();

    if (data.statusCode !== 200) {
      throw new ApiError(
        data.message || 'Failed to fetch form schema',
        data.statusCode,
        response.statusText,
        data
      );
    }

    if (!response.ok) {
      throw new ApiError(
        data.message || 'Failed to fetch form schema',
        response.status,
        response.statusText,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Failed to fetch form schema')
    );
  }
}

/**
 * Save Form Response types
 */
export interface SaveFormRequest {
  tester_email: string;
  patient_id?: number;
  appointment_id?: number;
  form_data: FormData;
  analysis_result?: AnalysisResponse;
  session_id?: string;
  completion_time_seconds?: number;
  primary_root_cause?: string;
  secondary_root_cause?: string;
  root_cause_explanation?: string;
  other_information?: string;
}

export interface SaveFormResponse {
  statusCode: number;
  message: string;
  data: {
    id: number;
    session_id: string;
    patient_id: number | null;
    appointment_id: number | null;
    status: string;
    created_at: string;
    answers_saved?: number;
    skipped_fields?: number;
    missing_fields?: number;
  };
}

/**
 * Save DC Copilot Form Response to user_history_new
 * Saves complete form data including answers and analysis results
 * Uses hybrid approach: key fields in columns, all answers in dc_copilot_form_answers
 * @param request - Form data and metadata to save
 * @returns Save response with created record ID
 * @throws ApiError if save fails
 */
export async function saveFormResponse(request: SaveFormRequest): Promise<SaveFormResponse> {
  try {
    const authBaseUrl = API_CONFIG.AUTH_BASE_URL;
    const saveUrl = `${authBaseUrl}/api/v1/admin/dc-copilot-form/save-complete`;
    
    const response = await fetch(saveUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('dc-copilot-token')}`,
      },
      body: JSON.stringify(request),
    });

    const data: SaveFormResponse = await response.json();

    if (data.statusCode !== 200 && data.statusCode !== 201) {
      throw new ApiError(
        data.message || 'Failed to save form response',
        data.statusCode,
        response.statusText,
        data
      );
    }

    if (!response.ok) {
      throw new ApiError(
        data.message || 'Failed to save form response',
        response.status,
        response.statusText,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Failed to save form response')
    );
  }
}

/**
 * Get Form Data Response - For prefilling existing form
 */
export interface GetFormDataResponse {
  status: number;
  message: string;
  data: {
    id: number;
    user_id: number;
    patient_id: number;
    doctor_id?: number;
    appointment_id?: number;
    session_id?: string;
    tester_email?: string;
    form_version?: string;
    status?: string;
    form_data: {
      full_name?: string;
      age?: number;
      city?: string;
      height_cm?: number | string;
      height_feet?: number;
      height_inches?: number;
      weight_kg?: number;
      occupation?: string;
      relationship_status?: string;
      current_sexual_activity?: string;
      first_consultation?: string;
      previous_treatments?: string[];
      main_issue?: string;
      additional_info?: string;
      ed_duration?: string;
      ed_gets_erections?: string;
      ed_partner_arousal_speed?: string;
      ed_partner_maintenance?: string;
      ed_partner_hardness?: string;
      ed_morning_erections?: string;
      ed_masturbation_imagination?: string;
      pe_duration?: string;
      pe_partner_ejaculation_time?: string;
      pe_partner_penile_sensitivity?: string;
      pe_partner_masturbation_control?: string;
      masturbation_method?: string;
      masturbation_grip?: string;
      masturbation_frequency?: string;
      porn_frequency?: string;
      performance_anxiety?: string;
      multiple_partners?: string;
      issues_all_partners?: string;
      partner_response?: string;
      medical_conditions?: string[];
      medical_conditions_other?: string;
      current_medications?: string[];
      current_medications_other?: string;
      spinal_genital_surgery?: string;
      emergency_red_flags?: string;
      alcohol_frequency?: string;
      smoking_frequency?: string;
      substance_consumption?: string;
      stress_level?: string;
      sleep_quality?: string;
      physical_activity?: string;
      primary_root_cause?: string;
      secondary_root_cause?: string;
      root_cause_explanation?: string;
      other_information?: string;
      [key: string]: any;
    };
    analysis_result?: any;
    treatment_package?: number | null;
    treatment_recommendation?: string | null;
    type_of_sexologist_recommended?: string | null;
    reason_of_treatment_not_started?: string | null;
    status_of_lead?: string | null;
    discount_offered?: string | null;
    trigger_of_treatment?: string | null;
    comments?: string | null;
    upload_file?: string | null;
    lock_in?: string | null;
    follow_up_date?: string | null;
    follow_up_time?: string | null;
    is_popup_filled?: boolean;
    created_at?: string;
    updated_at?: string;
  } | null;
}

/**
 * Get existing form data for prefilling
 * @param userId - User ID to fetch form data for
 * @param appointmentId - Optional appointment ID
 * @returns Form data response for prefilling, or null data if not found
 */
export async function getFormData(userId: number, appointmentId?: number): Promise<GetFormDataResponse> {
  try {
    const authBaseUrl = API_CONFIG.AUTH_BASE_URL;
    let url = `${authBaseUrl}/api/v1/admin/dc-copilot-form/get-complete?user_id=${userId}`;
    if (appointmentId) {
      url += `&appointment_id=${appointmentId}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('dc-copilot-token')}`,
      },
    });

    // Handle 404 - API endpoint doesn't exist or no data found
    if (response.status === 404) {
      console.log('Form data not found (404) - treating as new form');
      return {
        status: 200,
        message: 'No existing form data found',
        data: null,
      };
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.warn('API returned non-JSON response - treating as no data');
      return {
        status: 200,
        message: 'No existing form data found',
        data: null,
      };
    }

    const data: GetFormDataResponse = await response.json();

    // Note: API returns status 200 even if no data found (data will be null)
    if (!response.ok) {
      // For other errors, return null data instead of throwing
      console.warn('API error:', data.message || response.statusText);
      return {
        status: response.status,
        message: data.message || 'Failed to fetch form data',
        data: null,
      };
    }

    return data;
  } catch (error) {
    // Don't throw errors for this API - just return null data
    // This allows the form to proceed with new entry creation
    console.warn('Error fetching form data:', error);
    return {
      status: 500,
      message: 'Error fetching form data',
      data: null,
    };
  }
}

/**
 * Update Form Request - For updating existing form
 */
export interface UpdateFormRequest {
  id: number;
  form_data: FormData & { [key: string]: any };
  primary_root_cause?: string;
  secondary_root_cause?: string;
  root_cause_explanation?: string;
  other_information?: string;
  analysis_result?: any;
}

/**
 * Update Form Response
 */
export interface UpdateFormResponse {
  statusCode: number;
  message: string;
  data: {
    id: number;
    user_id: number;
    status: string;
    updated_at: string;
    answers_updated?: number;
  };
}

/**
 * Update existing form data
 * @param request - Form data to update
 * @returns Update response with updated record
 */
export async function updateFormData(request: UpdateFormRequest): Promise<UpdateFormResponse> {
  try {
    const authBaseUrl = API_CONFIG.AUTH_BASE_URL;
    const updateUrl = `${authBaseUrl}/api/v1/admin/dc-copilot-form/update-complete`;
    
    const response = await fetch(updateUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('dc-copilot-token')}`,
      },
      body: JSON.stringify(request),
    });

    const data: UpdateFormResponse = await response.json();

    if (data.statusCode !== 200 && data.statusCode !== 201) {
      throw new ApiError(
        data.message || 'Failed to update form data',
        data.statusCode,
        response.statusText,
        data
      );
    }

    if (!response.ok) {
      throw new ApiError(
        data.message || 'Failed to update form data',
        response.status,
        response.statusText,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Failed to update form data')
    );
  }
}

/**
 * Update User History Request
 */
export interface UpdateUserHistoryRequest {
  user_id: number;
  appointment_id?: number;
  treatment_package?: number | null;
  treatment_recommendation?: string | null;
  type_of_sexologist_recommended?: string | null;
  reason_of_treatment_not_started?: string | null;
  status_of_lead?: string | null;
  discount_offered?: string | null;
  trigger_of_treatment?: string | null;
  comments?: string | null;
  lock_in?: string | null;
  follow_up_date?: string | null;
  follow_up_time?: string | null;
}

/**
 * Update User History Response
 */
export interface UpdateUserHistoryResponse {
  statusCode: number;
  message: string;
  data?: {
    id: number;
    user_id: number;
    appointment_id?: number;
    [key: string]: any;
  };
}

/**
 * Update User History
 * Updates user history with session form data
 * @param request - User history data to update
 * @returns Update response with updated record
 * @throws ApiError if update fails
 */
export async function updateUserHistory(request: UpdateUserHistoryRequest): Promise<UpdateUserHistoryResponse> {
  try {
    const authBaseUrl = API_CONFIG.AUTH_BASE_URL;
    const updateUrl = `${authBaseUrl}/api/v1/admin/dc-copilot-form/update-user-history`;
    
    const response = await fetch(updateUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('dc-copilot-token')}`,
      },
      body: JSON.stringify(request),
    });

    const data: UpdateUserHistoryResponse = await response.json();

    if (data.statusCode !== 200 && data.statusCode !== 201) {
      throw new ApiError(
        data.message || 'Failed to update user history',
        data.statusCode,
        response.statusText,
        data
      );
    }

    if (!response.ok) {
      throw new ApiError(
        data.message || 'Failed to update user history',
        response.status,
        response.statusText,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Failed to update user history')
    );
  }
}

/**
 * Export types for TypeScript support
 */
export type { ApiResponse, RequestConfig } from './api/types';




